// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The glue between a real `XRFrame` and everything in this directory.
//
// This is the only file that touches the DOM's WebXR types, and it exists so that nothing else
// has to. Everything above it takes narrow structural ports and runs in Node; everything below it
// is the browser. The seam is here, deliberately and in one place, because the alternative is
// `XRFrame` in the signature of every module and a directory nobody can test.
//
// What it does per frame: find the viewer, find the surface the reticle is on, find the light
// estimate, work out whether a palm is up, and hand all of it to `XRRuntime`.
//
// **The hit-test source is asynchronous and the frame loop is not.** `requestHitTestSource`
// returns a promise, and a frame callback cannot wait for one. So it is requested once when the
// session starts and used from whenever it arrives — which means the first second or so of an MR
// session has no reticle, which is exactly what the `scanning` phase is for.

import { pickFloor, type PlaneReading } from './floorPlane'
import type { AvatarPose } from '../net/poseStream'
import type { XRFrameInput } from './XRRuntime'
import type { ProbeState } from './viewerRay'
import type { XRInputSourcePort } from './xrInput'
import type { BoundControls } from './cabinetRig'

/** The DOM types this file needs, named locally so nothing else imports them. */
interface DomXRSession {
  readonly inputSources: ArrayLike<unknown> & Iterable<unknown>
  readonly visibilityState?: string
  requestReferenceSpace: (type: string) => Promise<unknown>
  requestHitTestSource?: (options: { space: unknown }) => Promise<DomHitTestSource>
  requestLightProbe?: () => Promise<unknown>
  addEventListener?: (type: string, listener: (event: DomXRInputSourceEvent) => void) => void
  removeEventListener?: (type: string, listener: (event: DomXRInputSourceEvent) => void) => void
}

/** The slice of a DOM `XRInputSourceEvent` the pinch listener reads. */
interface DomXRInputSourceEvent {
  readonly inputSource?: { readonly hand?: unknown; readonly handedness?: string } | null
}

interface DomHitTestSource {
  cancel?: () => void
}

/**
 * The slice of the DOM `XRFrame` this file reads.
 *
 * **Every pose here is `null`-able, because the DOM's are.** `getViewerPose`, `getPose` and
 * `getJointPose` all return `XRPose?` — `null` whenever the runtime cannot place the thing this
 * frame: a head whose tracking has just been lost, a joint the cameras cannot see, the grip of a
 * pointer the system raised for one of its own overlays. A port typed `| undefined` invites a
 * `=== undefined` guard that `null` walks straight through, and the next line dereferences it.
 * That is one exception inside the session's animation callback, and Three asks for the next
 * frame *after* the callback returns — so the picture stops, black, for the rest of the session.
 * `xrInput.ts` learned this first (`XRFramePort`); the readers below normalise with `?? undefined`
 * at the call so the rest of the function keeps its one spelling of "absent".
 */
interface DomXRFrame {
  getViewerPose: (space: unknown) => DomPose | undefined | null
  getPose: (space: unknown, base: unknown) => DomPose | undefined | null
  /** WebXR Plane Detection: the room scan's planes, on a session granted `plane-detection`. */
  detectedPlanes?: Iterable<DomXRPlane> | undefined | null
  getJointPose?: (joint: unknown, base: unknown) => DomPose | undefined | null
  getHitTestResults?: (source: DomHitTestSource) => readonly DomHitTestResult[]
  getLightEstimate?: (probe: unknown) => DomLightEstimate | undefined | null
}

interface DomHitTestResult {
  getPose: (space: unknown) => DomPose | undefined | null
}

interface DomPose {
  readonly transform: {
    readonly position: { x: number; y: number; z: number }
    readonly orientation?: { x: number; y: number; z: number; w: number }
  }
}

/** An `XRPlane`: its own space (Y up along the normal), an outline in that space, and what it is. */
interface DomXRPlane {
  readonly planeSpace: unknown
  readonly polygon?: Iterable<{ x: number; y: number; z: number }> | undefined
  readonly orientation?: 'horizontal' | 'vertical' | undefined
  readonly semanticLabel?: string | undefined
}

interface DomLightEstimate {
  readonly primaryLightIntensity?: { x: number; y: number; z: number }
  readonly sphericalHarmonicsCoefficients?: Float32Array
}

/**
 * The per-session things that have to be requested once and used from a frame callback.
 *
 * Held together because they share a lifetime: they are created when the session starts, they are
 * useless afterwards, and forgetting to cancel a hit-test source leaks a runtime resource that
 * outlives the page's interest in it.
 */
export class XRFrameSources {
  private hitTest: DomHitTestSource | undefined
  private lightProbe: unknown
  private viewerSpace: unknown
  private cancelled = false
  private session: DomXRSession | undefined
  /**
   * What the session's own `selectstart`/`selectend` last said about each hand (13 Sep 2026).
   *
   * **The headset's pinch, not a ruler between two joints.** For a hand input source the primary
   * action *is* the pinch, detected by the runtime's hand model — the same gesture that presses
   * every button in the headset's own interface. `xrInput.ts` measured thumb-to-index instead, and
   * on the device the author's pointing hand never read open again after its first pinch, so the
   * next real pinch was no edge. A hand these events have never named stays `undefined`, and the
   * ruler decides for it; controllers and transient pointers are not hands and are not listened to.
   */
  private readonly pinched: { left?: boolean; right?: boolean } = {}

  private readonly onSelectStart = (event: DomXRInputSourceEvent): void => {
    this.notePinch(event, true)
  }

  private readonly onSelectEnd = (event: DomXRInputSourceEvent): void => {
    this.notePinch(event, false)
  }

  private notePinch(event: DomXRInputSourceEvent, down: boolean): void {
    const source = event.inputSource
    if (source === undefined || source === null || source.hand === undefined || source.hand === null) return
    if (source.handedness === 'left' || source.handedness === 'right') this.pinched[source.handedness] = down
  }

  /** The runtime's pinch per hand, for `HandTracker.advance`. A fresh object: the report is read later. */
  pinch(): { left?: boolean; right?: boolean } {
    return {
      ...(this.pinched.left === undefined ? {} : { left: this.pinched.left }),
      ...(this.pinched.right === undefined ? {} : { right: this.pinched.right }),
    }
  }
  /** Tri-state, because "not yet" and "never" need opposite responses. See `viewerRay.ts`. */
  private probeState: ProbeState = 'pending'

  /**
   * Whether the runtime's surface probe is going to answer.
   *
   * **`pending` until `open` has settled**, which is the whole reason this is not a boolean:
   * `requestHitTestSource` is a promise, so for the first frames of every MR session there is no
   * source and no refusal either. A caller that read the absence as a refusal would substitute a
   * floor plane on frame one of every session and then hand the reticle back a moment later,
   * which the player sees as the machine jumping.
   */
  get probe(): ProbeState {
    return this.probeState
  }

  /**
   * Ask for everything the mode needs. Never throws: each of these is optional in `features.ts`,
   * and a runtime that refuses one is a runtime the product already knows how to run without.
   */
  async open(session: unknown, mode: 'immersive-vr' | 'immersive-ar'): Promise<void> {
    const s = session as DomXRSession
    // Before the first await, so a pinch made while the probes are being requested is not lost.
    this.session = s
    s.addEventListener?.('selectstart', this.onSelectStart)
    s.addEventListener?.('selectend', this.onSelectEnd)
    try {
      this.viewerSpace = await s.requestReferenceSpace('viewer')
    } catch {
      this.viewerSpace = undefined
    }
    if (mode !== 'immersive-ar') {
      // VR has no reticle and asks for no surfaces, so there is nothing to be pending about.
      this.probeState = 'refused'
      return
    }

    if (s.requestHitTestSource !== undefined && this.viewerSpace !== undefined) {
      try {
        const source = await s.requestHitTestSource({ space: this.viewerSpace })
        // The session may have ended while we were waiting, in which case the source is already
        // dead and holding it would keep a reference to a finished session.
        if (this.cancelled) source.cancel?.()
        else this.hitTest = source
      } catch {
        this.hitTest = undefined
      }
    }
    // Settled either way. On Quest the ordinary cause of a refusal is a player declining the
    // spatial-data prompt: the feature is requested optionally, so the session still starts —
    // just with no way to probe a surface for the rest of it.
    this.probeState = this.hitTest === undefined ? 'refused' : 'ready'
    if (s.requestLightProbe !== undefined) {
      try {
        this.lightProbe = await s.requestLightProbe()
      } catch {
        this.lightProbe = undefined
      }
    }
  }

  close(): void {
    this.cancelled = true
    this.probeState = 'pending'
    this.hitTest?.cancel?.()
    this.hitTest = undefined
    this.lightProbe = undefined
    this.viewerSpace = undefined
    this.session?.removeEventListener?.('selectstart', this.onSelectStart)
    this.session?.removeEventListener?.('selectend', this.onSelectEnd)
    this.session = undefined
    delete this.pinched.left
    delete this.pinched.right
  }

  /** Where the reticle should be, this frame. */
  surface(frame: unknown, referenceSpace: unknown): XRFrameInput['surface'] {
    const f = frame as DomXRFrame
    const source = this.hitTest
    if (source === undefined || f.getHitTestResults === undefined) return undefined
    const results = f.getHitTestResults(source)
    const first = results[0]
    if (first === undefined) return undefined
    const pose = first.getPose(referenceSpace) ?? undefined
    if (pose === undefined) return undefined
    const p = pose.transform.position
    const yaw = yawOf(pose)
    return yaw === undefined ? { x: p.x, y: p.y, z: p.z } : { x: p.x, y: p.y, z: p.z, yaw }
  }

  /** The room's light, if the runtime granted estimation. */
  light(frame: unknown): XRFrameInput['light'] {
    const f = frame as DomXRFrame
    if (this.lightProbe === undefined || f.getLightEstimate === undefined) return undefined
    const estimate = f.getLightEstimate(this.lightProbe) ?? undefined
    if (estimate === undefined) return undefined
    // The first spherical-harmonic coefficient is the ambient term; it is the only one this
    // product uses, because a tint needs a colour and not a direction.
    const sh = estimate.sphericalHarmonicsCoefficients
    const r = sh?.[0] ?? 1
    const g = sh?.[1] ?? 1
    const b = sh?.[2] ?? 1
    const i = estimate.primaryLightIntensity
    const intensity = i === undefined ? (r + g + b) / 3 : (i.x + i.y + i.z) / 3
    return { r, g, b, intensity }
  }
}

/**
 * Assemble one frame's input.
 *
 * `palmUp` is computed here rather than in `xrInput.ts` because it is the one thing that needs a
 * joint's *orientation* rather than its position, and giving the hand tracker orientations it
 * would otherwise never read would double the size of its hot path for one gesture.
 */
export function frameInput(
  frame: unknown,
  referenceSpace: unknown,
  session: unknown,
  sources: XRFrameSources,
  mode: 'immersive-vr' | 'immersive-ar',
): XRFrameInput {
  const f = frame as DomXRFrame
  const s = session as DomXRSession
  const inputSources = [...s.inputSources] as XRInputSourcePort[]

  // `null` when tracking is lost for a frame. A head that is absent for a frame is a frame with no
  // head, which every reader downstream already has an answer for; a throw here is a black session.
  const viewer = f.getViewerPose(referenceSpace) ?? undefined
  const head =
    viewer === undefined
      ? undefined
      : {
          x: viewer.transform.position.x,
          y: viewer.transform.position.y,
          z: viewer.transform.position.z,
          yaw: headYaw(viewer),
          // **The forward vector itself, not just its yaw.** M5-33 places the machine along the
          // player's gaze, and reconstructing a direction from a yaw and a pitch means agreeing
          // with whichever rotation convention those were extracted under — which is the exact
          // class of sign error that put a cabinet behind a player earlier in this project. The
          // vector is already in hand here; passing it costs nothing and cannot be misread.
          forward: headForward(viewer),
          // Pitch and roll are read here and only used by the pose stream (M8-03). They cost two
          // multiplies off an orientation this function already has in hand, and without them a
          // remote player's head is a ball that never nods — which is most of what a head does
          // while somebody watches a ball drain.
          ...tilt(viewer),
        }

  const surface = mode === 'immersive-ar' ? sources.surface(frame, referenceSpace) : undefined
  const light = mode === 'immersive-ar' ? sources.light(frame) : undefined
  const floorY = head === undefined ? undefined : pickFloor(floorPlanesOf(f, referenceSpace), head)

  return {
    frame: f as XRFrameInput['frame'],
    referenceSpace,
    inputSources,
    head,
    ...(surface === undefined ? {} : { surface }),
    surfaceProbe: sources.probe,
    ...(light === undefined ? {} : { light }),
    visible: s.visibilityState === undefined || s.visibilityState === 'visible',
    palmUp: palmUpOf(f, referenceSpace, inputSources),
    palmNormal: palmNormalOf(f, referenceSpace, inputSources),
    ...(floorY === undefined ? {} : { floorY }),
    bound: boundControlsOf(inputSources),
    wrists: wristsOf(f, referenceSpace, inputSources),
    gripHeld: inputSources.some((source) => source.gamepad?.buttons[1]?.pressed === true),
    pinch: sources.pinch(),
  }
}

/**
 * A viewer's pitch and roll, radians.
 *
 * Extracted from the quaternion directly rather than through a matrix or Euler helper, in the same
 * style as `headYaw` and `palmUpOf` above, because this file is the one place that touches DOM XR
 * types and adding a maths dependency to it for six lines would be a poor trade. The order is YXZ
 * — yaw, then pitch, then roll — which is how a head turns, and it is the order `avatars.ts` puts
 * back on the mesh.
 */
function tilt(pose: DomPose): { pitch: number; roll: number } {
  const q = pose.transform.orientation
  if (q === undefined) return { pitch: 0, roll: 0 }
  // The forward vector's Y component gives pitch directly; roll is the tilt of the right vector.
  const fy = 2 * (q.y * q.z - q.w * q.x)
  const pitch = Math.asin(Math.max(-1, Math.min(1, -fy)))
  const roll = Math.atan2(
    2 * (q.x * q.y + q.w * q.z),
    1 - 2 * (q.x * q.x + q.z * q.z),
  )
  return { pitch, roll }
}

/**
 * Both wrists, for the pose stream (M8-03).
 *
 * **The wrist joint for a hand and the grip pose for a controller**, which are the same thing for
 * this purpose: where the base of the hand is and which way it is turned. A controller player's
 * remote avatar should not be handless — they are holding the machine's buttons exactly as a
 * hand-tracking player is, and 06 §4 says "head-and-hands" without qualifying which input it came
 * from.
 *
 * `null` for a hand that is not tracked, and that is not the same as a hand at the origin: a hand
 * put down should disappear rather than appear inside the cabinet. `poseStream.ts` carries the
 * distinction the whole way.
 */
function wristsOf(
  frame: DomXRFrame,
  referenceSpace: unknown,
  sources: readonly XRInputSourcePort[],
): XRFrameInput['wrists'] {
  let left: AvatarPose | null = null
  let right: AvatarPose | null = null
  for (const source of sources) {
    const joints = source.hand
    let pose: DomPose | undefined
    if (joints !== undefined && joints !== null && frame.getJointPose !== undefined) {
      pose = frame.getJointPose(joints.get('wrist'), referenceSpace) ?? undefined
    }
    if (pose === undefined) {
      const grip = (source as { gripSpace?: unknown }).gripSpace
      if (grip !== undefined && grip !== null) pose = frame.getPose(grip, referenceSpace) ?? undefined
    }
    if (pose === undefined) continue
    const at: AvatarPose = {
      x: pose.transform.position.x,
      y: pose.transform.position.y,
      z: pose.transform.position.z,
      yaw: headYaw(pose),
      ...tilt(pose),
    }
    if (source.handedness === 'left') left = at
    else if (source.handedness === 'right') right = at
  }
  return { left, right }
}

/**
 * The `xr-standard` button indices this file reads.
 *
 * Named rather than spelled as numbers at the call sites, because `buttons[4]` is unreadable and
 * `buttons[5]` is unreadable in a way that looks correct. The mapping is the WebXR gamepad
 * profile's, which every Quest controller reports.
 */
const XR_STANDARD = {
  trigger: 0,
  squeeze: 1,
  /**
   * The thumbstick pressed in, which is the **other** flipper (G2-10, 06 §6's one-handed play).
   *
   * The one button left on an `xr-standard` controller once the trigger is a flipper, A/X is the
   * plunger and B/Y is start — and the squeeze is the wrist menu, which is a gesture a player makes
   * deliberately and must not double as a flipper.
   */
  thumbstick: 3,
  /** A on the right controller, X on the left. */
  primary: 4,
  /** B on the right, Y on the left. */
  secondary: 5,
} as const

/** Thumbstick axes. 0 and 1 are a touchpad most controllers do not have. */
const STICK = { x: 2, y: 3 } as const

/** Below this a stick is at rest; the rig's own nudge threshold is higher again. */
const STICK_DEADZONE = 0.15

/**
 * Comfort mode's controller bindings (XR-007).
 *
 * **This function is the whole of a bug that had shipped.** `BoundControls` was declared, threaded
 * through `XRRuntime` into `CabinetRig.advance`, and filled in by nothing except unit tests — and
 * in comfort mode the rig skips `advanceHand` entirely and drives everything from these bindings.
 * So turning on the one mode a seated player needs produced a machine with no flippers, no
 * plunger, no start button and no nudge. Every unit test passed, because each one handed the rig
 * the bindings the product never did.
 *
 * The mapping is 06 §6 read literally: *"Flippers on the triggers, plunge on A/X held and
 * released, nudge on the left thumbstick, start on B/Y."*
 *
 * **Either hand's A/X plunges and either hand's B/Y starts**, because comfort mode has one set of
 * bindings rather than one per hand, and a seated player should not have to remember which
 * controller the start button is on. The flippers follow the obvious rule — left trigger is the
 * left flipper — and each controller *also* carries the other one on its thumbstick click, so
 * either controller alone is a complete pair (G2-10). See the body for why that button and no
 * other, and why there is no mode wrapped around it.
 *
 * Returned always rather than only in comfort mode. The rig ignores it unless comfort is on, and a
 * frame loop that had to know about a mode it does not own is a second owner of that mode.
 */
function boundControlsOf(sources: readonly XRInputSourcePort[]): BoundControls {
  let leftFlipper = false
  let rightFlipper = false
  let start = false
  let plunge = false
  let nudgeX = 0
  let nudgeY = 0

  for (const source of sources) {
    const pad = source.gamepad
    // **`null` as well as `undefined`** — the DOM attribute is `Gamepad?`, and it is `null` on a
    // tracked hand and on the transient pointer the system raises for its own overlays. The first
    // device session with the keyboard overlay up threw here on its first frame (`Cannot read
    // properties of null (reading 'buttons')`) and drew nothing again: sound, and a black picture.
    if (pad === undefined || pad === null) continue
    const pressed = (index: number): boolean => pad.buttons[index]?.pressed === true
    // **Each controller works both flippers, always, with no mode to find** (G2-10, 06 §6's
    // "both flippers can be bound to one controller or one hand").
    //
    // The trigger is its own side and the thumbstick click is the other. That is `desktop.ts`'s
    // argument, one input scheme over: *"a mode is something you have to know exists"*, and a
    // player who cannot use two controllers is exactly the player least likely to go looking
    // through a settings page for permission to play. A two-handed player never presses this and
    // nothing about their controls changes.
    //
    // Not the squeeze, which opens the wrist menu; not A/X or B/Y, which are the plunger and start.
    // The thumbstick *click* is a different button from its axes, so the left stick going on being
    // the nudge costs nothing here.
    if (source.handedness === 'left') {
      if (pressed(XR_STANDARD.trigger)) leftFlipper = true
      if (pressed(XR_STANDARD.thumbstick)) rightFlipper = true
    }
    if (source.handedness === 'right') {
      if (pressed(XR_STANDARD.trigger)) rightFlipper = true
      if (pressed(XR_STANDARD.thumbstick)) leftFlipper = true
    }
    if (pressed(XR_STANDARD.primary)) plunge = true
    if (pressed(XR_STANDARD.secondary)) start = true

    if (source.handedness !== 'left') continue
    const x = pad.axes?.[STICK.x] ?? 0
    const y = pad.axes?.[STICK.y] ?? 0
    if (Math.hypot(x, y) < STICK_DEADZONE) continue
    nudgeX = x
    // **Negated, and this is the sign that would be wrong.** `xr-standard` reports a stick pushed
    // *forward* as negative Y, and the playfield's +Y points up-table, away from the player — the
    // same direction `desktop.ts` gives the W key. Without this a player nudging away from
    // themselves would pull the ball toward the drain.
    nudgeY = -y
  }

  return { leftFlipper, rightFlipper, start, plunge, nudgeX, nudgeY }
}

/**
 * The room scan's planes, in the presented space (`floorPlane.ts`, 14 Sep 2026).
 *
 * Each plane's pose puts its own origin in the presented space and its Y along the normal, so a
 * floor's height is its origin's y; the outline is the polygon's corners rotated by that pose
 * and dropped onto the ground, for "which floor is the player standing on". A runtime without
 * `detectedPlanes`, or a frame the scan has nothing for, is an empty list.
 */
function floorPlanesOf(f: DomXRFrame, referenceSpace: unknown): PlaneReading[] {
  const planes = f.detectedPlanes
  if (planes === undefined || planes === null) return []
  const out: PlaneReading[] = []
  for (const plane of planes) {
    let pose: DomPose | undefined | null
    try {
      pose = f.getPose(plane.planeSpace, referenceSpace)
    } catch {
      continue
    }
    if (pose === undefined || pose === null) continue
    const p = pose.transform.position
    const q = pose.transform.orientation ?? { x: 0, y: 0, z: 0, w: 1 }
    const corners: { x: number; z: number }[] = []
    for (const c of plane.polygon ?? []) {
      const r = rotate(q, c)
      corners.push({ x: r.x + p.x, z: r.z + p.z })
    }
    out.push({ y: p.y, label: plane.semanticLabel ?? undefined, orientation: plane.orientation ?? undefined, corners })
  }
  return out
}

/** `v` rotated by the unit quaternion `q`: v + 2w(q×v) + 2(q×(q×v)). */
function rotate(
  q: { x: number; y: number; z: number; w: number },
  v: { x: number; y: number; z: number },
): { x: number; y: number; z: number } {
  const tx = 2 * (q.y * v.z - q.z * v.y)
  const ty = 2 * (q.z * v.x - q.x * v.z)
  const tz = 2 * (q.x * v.y - q.y * v.x)
  return {
    x: v.x + q.w * tx + (q.y * tz - q.z * ty),
    y: v.y + q.w * ty + (q.z * tx - q.x * tz),
    z: v.z + q.w * tz + (q.x * ty - q.y * tx),
  }
}

/**
 * How far the left palm is facing the sky, as a dot product with world up — or `undefined` when
 * there is no tracked left hand to ask.
 *
 * **The palm is the joint's −Y.** WebXR Hand Input §XRJointSpace: *"the native origin has its −Y
 * direction pointing perpendicular to the skin, outwards from the palm, and −Z direction pointing
 * along their associated bone, away from the wrist."* The native client reads the same axis
 * (`XRHandOrientationUtility.PalmDirection` is `rootRotation × (0, −1, 0)`), so this is the third
 * road to the same expression. Rotating (0, −1, 0) by the joint's orientation and taking the Y
 * component is one entry of the rotation matrix, negated: `2(x² + z²) − 1`.
 *
 * **The first expression here was the fingertip direction, and it took a device day to notice.**
 * It rotated (0, 0, −1) — the bone, not the palm — and then took a component that was not even the
 * Y one, so what it measured was how much the palm faced the reference space's +Z: an author who
 * happened to be facing −Z with the palm turned toward their face and the fingers hanging down
 * scored 1 and the menu opened, which is how the wrist menu was "confirmed" on a headset; the same
 * author turned ninety degrees, or holding the palm flat to the ceiling as every document says to,
 * scored 0 and reported that the menu did not exist. The palm-to-the-face pose is also the Quest's
 * own system gesture, so the runtime was claiming the hand at the exact moment the old expression
 * wanted it. `test/xr/palm-up.test.ts` pins the ceiling, the floor, the old pose, and the yaw.
 */
function palmUpOf(
  frame: DomXRFrame,
  referenceSpace: unknown,
  sources: readonly XRInputSourcePort[],
): number | undefined {
  if (frame.getJointPose === undefined) return undefined
  for (const source of sources) {
    if (source.handedness !== 'left' || source.hand === undefined || source.hand === null) continue
    const pose = frame.getJointPose(source.hand.get('middle-finger-metacarpal'), referenceSpace)
    const q = pose?.transform.orientation
    if (q === undefined) continue
    // The Y component of the rotated (0, −1, 0): −(1 − 2(x² + z²)). Facing-independent — a yaw
    // about world up leaves it alone — which the old expression was not.
    return 2 * (q.x * q.x + q.z * q.z) - 1
  }
  // No tracked left hand. **Not `-1`** — see `WristMenu.advance`: reporting "palm not up" for
  // "no palm at all" closed a controller player's menu on the frame their grip opened it.
  return undefined
}

/**
 * The left palm's normal in the reference space: (0, −1, 0) rotated by the same joint `palmUpOf`
 * reads, whose Y component is exactly `palmUpOf`'s number. With the head it says how squarely the
 * palm faces the player (`palmFacing`, the 1.0.110 round). Undefined with no tracked left hand.
 */
function palmNormalOf(
  frame: DomXRFrame,
  referenceSpace: unknown,
  sources: readonly XRInputSourcePort[],
): { x: number; y: number; z: number } | undefined {
  if (frame.getJointPose === undefined) return undefined
  for (const source of sources) {
    if (source.handedness !== 'left' || source.hand === undefined || source.hand === null) continue
    const pose = frame.getJointPose(source.hand.get('middle-finger-metacarpal'), referenceSpace)
    const q = pose?.transform.orientation
    if (q === undefined) continue
    // The second column of the rotation matrix, negated.
    return {
      x: -(2 * (q.x * q.y - q.w * q.z)),
      y: 2 * (q.x * q.x + q.z * q.z) - 1,
      z: -(2 * (q.y * q.z + q.w * q.x)),
    }
  }
  return undefined
}

/** Yaw about world up, from a pose's orientation. 0 looks down −Z, matching `Placement`. */
/**
 * Which way the viewer is looking, as a unit vector in the reference space.
 *
 * `(0, 0, −1)` rotated by the orientation — the same three expressions `headYaw` and `tilt`
 * already compute between them, gathered into the one form that has no convention to get wrong.
 * A pose with no orientation looks straight ahead, which is the only answer that leaves a machine
 * where a player without a tracked head could find it.
 */
function headForward(pose: DomPose): { x: number; y: number; z: number } {
  const q = pose.transform.orientation
  if (q === undefined) return { x: 0, y: 0, z: -1 }
  return {
    x: -(2 * (q.x * q.z + q.w * q.y)),
    y: -(2 * (q.y * q.z - q.w * q.x)),
    z: -(1 - 2 * (q.x * q.x + q.y * q.y)),
  }
}

function headYaw(pose: DomPose): number {
  const q = pose.transform.orientation
  if (q === undefined) return 0
  // The forward vector (0, 0, −1) rotated, then atan2 of its X and −Z.
  const fx = -(2 * (q.x * q.z + q.w * q.y))
  const fz = -(1 - 2 * (q.x * q.x + q.y * q.y))
  return Math.atan2(fx, -fz)
}

function yawOf(pose: DomPose): number | undefined {
  return pose.transform.orientation === undefined ? undefined : headYaw(pose)
}
